/**
 * FibiEspera.jsx · v101.196.0 · Aviso flotante para agendadores: clientes de WhatsApp en espera de un agente
 * (Fibi no pudo resolver) en su sucursal, con el resumen del problema. Consulta /api/v1/fibi/espera cada 20 s.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { MessageSquareWarning, X, ChevronRight } from "lucide-react";

const ROLES = ["super_admin", "admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"];

export default function FibiEspera() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [oculto, setOculto] = useState("");
  const prev = useRef(0);
  const activo = !!user && ROLES.includes(String(user.role || "").toLowerCase());
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/fibi/espera");
      setD(data);
      const n = (data?.total || 0) + (data?.mis_pendientes || 0);
      if (n > prev.current && typeof document !== "undefined" && document.hidden && "Notification" in window
          && Notification.permission === "granted") {
        try { new Notification("Clientes en espera (WhatsApp)", { body: data.items?.[0]?.resumen || data.items?.[0]?.motivo || "Hay un cliente esperando" }); } catch (e) { /* sin notificación */ }
      }
      prev.current = n;
    } catch (e) { /* silencioso: el aviso no debe molestar si falla */ }
  }, []);
  useEffect(() => {
    if (!activo) return undefined;
    load();
    const t = setInterval(load, 20000);
    if ("Notification" in window && Notification.permission === "default") { try { Notification.requestPermission(); } catch (e) { /* nada */ } }
    return () => clearInterval(t);
  }, [activo, load]);
  if (!activo || !d) return null;
  const firma = `${d.total}-${d.mis_pendientes}-${(d.items || []).map((x) => x.id).join(",")}`;
  if (!d.total && !d.mis_pendientes) return null;
  if (oculto === firma) return null;
  const ir = (id) => navigate(id ? `/whatsapp/conversaciones?c=${id}` : "/whatsapp/conversaciones");
  return (
    <div style={{ position: "fixed", left: 18, bottom: 18, zIndex: 60, width: 340, borderRadius: 18, background: "#FFFFFF",
      boxShadow: "0 12px 32px rgba(15,23,42,.22)", border: "2px solid #F59E0B", overflow: "hidden" }} data-testid="fibi-espera">
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "#FEF3C7" }}>
        <MessageSquareWarning size={18} color="#B45309" />
        <div style={{ flex: 1, fontWeight: 800, fontSize: 13, color: "#78350F" }}>
          {d.total ? `${d.total} cliente${d.total > 1 ? "s" : ""} en espera` : ""}
          {d.total && d.mis_pendientes ? " · " : ""}
          {d.mis_pendientes ? `${d.mis_pendientes} chat${d.mis_pendientes > 1 ? "s" : ""} tuyo${d.mis_pendientes > 1 ? "s" : ""} sin responder` : ""}
        </div>
        <button onClick={() => setOculto(firma)} style={{ border: 0, background: "transparent", cursor: "pointer" }} title="Ocultar hasta que llegue otro"><X size={16} color="#78350F" /></button>
      </div>
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {(d.items || []).slice(0, 5).map((x) => (
          <button key={x.id} onClick={() => ir(x.id)} style={{ width: "100%", textAlign: "left", border: 0, borderBottom: "1px solid #F1F5F9",
            background: "transparent", padding: "9px 12px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>{x.cliente || `Tel. ${x.telefono_corto}`}{x.task_number ? ` · OT ${x.task_number}` : ""}</div>
              <div style={{ fontSize: 11.5, color: "#475569" }}>{x.branch_name || "sin sucursal"} · {x.tipo === "falla" ? "Falla" : x.tipo === "agenda" ? "Agenda" : "Consulta"}: {x.motivo}</div>
              {x.resumen && <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 2 }}>{x.resumen.length > 140 ? `${x.resumen.slice(0, 140)}…` : x.resumen}</div>}
            </div>
            <ChevronRight size={16} color="#94A3B8" />
          </button>
        ))}
      </div>
      <button onClick={() => ir(null)} style={{ width: "100%", border: 0, background: "#0A3747", color: "#FFF", padding: "9px", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
        Abrir conversaciones
      </button>
    </div>
  );
}
